import { fixedUi } from '@/i18n/fixedUi';
import React, { useRef, useState } from 'react';
import RequiredMark from '@/tiktok-platform/components/RequiredMark';
import FieldHelpMark from '@/components/FieldHelpMark';
import { tiktokPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import { chartText } from '@/i18n/chartText';
import { useTranslation } from 'react-i18next';

const MAX_GUIDE_FILES = 35;
const MAX_GUIDE_BYTES = 50 * 1024 * 1024;

/** 翻译默认目标语种（排除源语言后提交） */
const DEFAULT_TARGET_LANGS = [
  'en', 'de', 'fr', 'es', 'it', 'nl', 'pl', 'pt', 'ja', 'ko', 'ar', 'th', 'vi', 'sv', 'da',
];

const GUIDE_UPLOAD_REQUIREMENTS = [
  '仅支持 PDF 格式，单个文件 ≤50MB。',
  '说明书应尽可能覆盖更多语言，有助于提升转化并降低客诉。',
  '平台要求说明书必须包含英文内容；语种由平台自动识别。',
  '「翻译多语言并上传」会先上传源文件，再调用平台翻译接口生成多语种 PDF。',
  '「从说明书制作工具选择」为卖家中心站内工具，本系统暂不内嵌，请在卖家中心制作后导出 PDF 再本地上传。',
];

async function pollLanguages(shopId, pdfId, { attempts = 12, intervalMs = 1500 } = {}) {
  let last = null;
  for (let i = 0; i < attempts; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    last = await tiktokPlatformAPI.getPublishInstructionsLanguages(shopId, pdfId);
    const status = Number(last?.status ?? last?.result?.status);
    const langs = Array.isArray(last?.languages)
      ? last.languages
      : (Array.isArray(last?.result?.languages) ? last.result.languages : []);
    if (status === 2 || langs.length) {
      return { languages: langs.map(String), status: status || 2, raw: last };
    }
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  const langs = Array.isArray(last?.languages) ? last.languages : [];
  return { languages: langs.map(String), status: Number(last?.status || 0), raw: last };
}

async function pollTranslation(shopId, translateId, { attempts = 20, intervalMs = 2000 } = {}) {
  let last = null;
  for (let i = 0; i < attempts; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    last = await tiktokPlatformAPI.getPublishInstructionsTranslationResult(shopId, translateId);
    const finished = Boolean(last?.finished ?? last?.result?.finished);
    const failReason = String(last?.failReason || last?.result?.failReason || '').trim();
    if (failReason) {
      throw new Error(failReason);
    }
    if (finished) {
      const pdfId = Number(last?.pdfId || last?.result?.pdfId || 0);
      const langs = Array.isArray(last?.languages)
        ? last.languages
        : (Array.isArray(last?.result?.languages) ? last.result.languages : []);
      return { pdfId, languages: langs.map(String), raw: last };
    }
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error(chartText('publishExtra.translateTimeout', '说明书翻译超时，请稍后在列表中核对或重新上传'));
}

/**
 * Tiktok 电子说明书：PDF → instructions.upload → 识别语种 →（可选）翻译 → productGuideFileReqs。
 * needGuideFile / needGuideLoading 目前恒为 false：TikTok 开放平台没有等价于 Temu cats mandatory 的
 * 类目必填查询接口，无法判断「类目是否必传说明书」；入参保留以便后续接入平台接口。
 */
export default function TiktokGuideFilePanel({
  shopId = '',
  needGuideFile = false,
  needGuideLoading = false,
  files = [],
  onChange,
  errorMessage = '',
}) {
  const inputRef = useRef(null);
  const translateInputRef = useRef(null);
  useTranslation('workbench');
  const [busy, setBusy] = useState(false);
  const [busyMode, setBusyMode] = useState('');
  const [uploadIntent, setUploadIntent] = useState('update'); // update | supplement
  const [sourceTab, setSourceTab] = useState('local'); // local | tool
  const [showRequirements, setShowRequirements] = useState(false);
  const list = Array.isArray(files) ? files : [];

  const appendGuide = (item) => {
    const next = [...list, item].slice(0, MAX_GUIDE_FILES);
    onChange?.(next);
  };

  const validateFile = (file) => {
    if (!shopId) {
      showToast(chartText('returnList.pickShop', fixedUi("请先选择店铺")), 'error');
      return false;
    }
    if (list.length >= MAX_GUIDE_FILES) {
      showToast(chartText('publishExtra.maxGuides', `最多上传 ${MAX_GUIDE_FILES} 个说明书文件`, { count: MAX_GUIDE_FILES }), 'warning');
      return false;
    }
    if (!file || !/\.pdf$/i.test(file.name || '')) {
      showToast(chartText('publishExtra.pdfOnly', fixedUi("仅支持上传 PDF")), 'error');
      return false;
    }
    if (file.size > MAX_GUIDE_BYTES) {
      showToast(chartText('publishExtra.pdfSize', fixedUi("说明书文件需在 50MB 以内")), 'error');
      return false;
    }
    return true;
  };

  const uploadPlain = async (file) => {
    if (!validateFile(file)) return;
    setBusy(true);
    setBusyMode('upload');
    try {
      const uploaded = await tiktokPlatformAPI.uploadPublishInstructions(shopId, file);
      const pdfId = Number(uploaded?.pdfId || uploaded?.result?.pdfId || 0);
      if (!(pdfId > 0)) throw new Error(chartText('publishExtra.noPdfId', '上传未返回 pdfId'));
      const polled = await pollLanguages(shopId, pdfId);
      appendGuide({
        fileName: String(uploaded?.fileName || file.name || `manual-${pdfId}.pdf`),
        pdfMaterialId: pdfId,
        languages: polled.languages.length ? polled.languages : ['en'],
        status: polled.status,
      });
      showToast(
        polled.languages.length
          ? chartText('publishExtra.uploadedLangs', `已上传说明书（语种：${polled.languages.join(', ')}）`, { langs: polled.languages.join(', ') })
          : chartText('publishExtra.uploadedPending', '已上传说明书（语种识别中，已默认 en，提交前请刷新核对）'),
        'success',
      );
    } catch (e) {
      showToast(e?.message || chartText('publishExtra.uploadFail', '说明书上传失败'), 'error');
    } finally {
      setBusy(false);
      setBusyMode('');
    }
  };

  const uploadAndTranslate = async (file) => {
    if (!validateFile(file)) return;
    setBusy(true);
    setBusyMode('translate');
    try {
      const uploaded = await tiktokPlatformAPI.uploadPublishInstructions(shopId, file);
      const pdfId = Number(uploaded?.pdfId || uploaded?.result?.pdfId || 0);
      if (!(pdfId > 0)) throw new Error(chartText('publishExtra.noPdfId', '上传未返回 pdfId'));
      const polled = await pollLanguages(shopId, pdfId);
      const sourceLang = polled.languages.includes('en')
        ? 'en'
        : (polled.languages[0] || 'en');
      const targets = DEFAULT_TARGET_LANGS.filter((code) => code !== sourceLang);
      const started = await tiktokPlatformAPI.startPublishInstructionsTranslation(shopId, pdfId, {
        selectedSourceLanguageCode: sourceLang,
        targetLanguageCodeList: targets,
      });
      const translateId = Number(started?.translateId || started?.result?.translateId || 0);
      if (!(translateId > 0)) throw new Error(chartText('publishExtra.noTranslateId', '翻译未返回 translateId'));
      const translated = await pollTranslation(shopId, translateId);
      const finalPdfId = Number(translated.pdfId || pdfId);
      const langs = translated.languages.length
        ? translated.languages
        : (polled.languages.length ? polled.languages : [sourceLang]);
      appendGuide({
        fileName: String(uploaded?.fileName || file.name || `manual-${finalPdfId}.pdf`),
        pdfMaterialId: finalPdfId,
        languages: langs,
        status: 2,
        translated: true,
      });
      showToast(chartText('publishExtra.translatedLangs', `已翻译并上传说明书（语种：${langs.join(', ')}）`, { langs: langs.join(', ') }), 'success');
    } catch (e) {
      showToast(e?.message || chartText('publishExtra.translateFail', '翻译多语言上传失败'), 'error');
    } finally {
      setBusy(false);
      setBusyMode('');
    }
  };

  return (
    <div className="tko-guide-file" data-field-error="guideFiles">
      <h4 className="tko-publish__subhead tko-th-with-help">
        {chartText('publishExtra.guideTitle', '电子说明书')}
        {needGuideFile ? <RequiredMark /> : null}
        <FieldHelpMark text={chartText('publishExtra.guideHelp', '按类目要求上传多语言 PDF 说明书。平台会识别语种；发品提交写入 productGuideFileReqs。必须包含英文内容。')} />
      </h4>
      {needGuideLoading ? (
        <p className="tko-wizard__inline-hint">{chartText('publishExtra.guideChecking', fixedUi("正在查询该类目是否必须上传说明书…"))}</p>
      ) : (
        <p className="tko-wizard__inline-hint">
          {needGuideFile
            ? chartText('publishExtra.guideRequired', '当前类目要求必传说明书。')
            : chartText('publishExtra.guideOptional', '按需上传；覆盖更多语言有助于转化与降低客诉（平台未提供类目必填查询，发布时以平台校验为准）。')}
        </p>
      )}
      {errorMessage ? <div className="tko-field__error" role="alert">{errorMessage}</div> : null}

      <div className="tko-guide-file__box">
        <div className="tko-guide-file__row">
          <span className="tko-guide-file__row-label">{chartText('publishExtra.uploadType', fixedUi("上传类型"))}</span>
          <div className="tko-guide-file__radios" role="radiogroup" aria-label={chartText('publishExtra.uploadType', fixedUi("上传类型"))}>
            <label className="tko-guide-file__radio">
              <input
                type="radio"
                name="guide-upload-intent"
                checked={uploadIntent === 'update'}
                onChange={() => setUploadIntent('update')}
              />
              <span>{chartText('publishExtra.intentUpdate', fixedUi("更新说明书文件"))}</span>
            </label>
            <label className="tko-guide-file__radio">
              <input
                type="radio"
                name="guide-upload-intent"
                checked={uploadIntent === 'supplement'}
                onChange={() => setUploadIntent('supplement')}
              />
              <span>{chartText('publishExtra.intentSupplement', fixedUi("补充或修改【安装/使用说明】和【安全信息说明】"))}</span>
            </label>
          </div>
        </div>

        {uploadIntent === 'supplement' ? (
          <p className="tko-wizard__inline-hint">
            {chartText('publishExtra.supplementHint', '发品流程以 PDF 说明书文件为准（写入 productGuideFileReqs）。安装/使用说明与安全信息的结构化文本编辑目前请在 Tiktok 卖家中心完成；此处仍可通过下方上传更新说明书文件。')}
          </p>
        ) : null}

        <div className="tko-guide-file__tabs" role="tablist" aria-label={chartText('publishExtra.guideSource', fixedUi("说明书来源"))}>
          <button
            type="button"
            role="tab"
            aria-selected={sourceTab === 'local'}
            className={`tko-guide-file__tab${sourceTab === 'local' ? ' is-active' : ''}`}
            onClick={() => setSourceTab('local')}
          >
            {chartText('publishExtra.fromLocal', '从本地上传')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={sourceTab === 'tool'}
            className={`tko-guide-file__tab${sourceTab === 'tool' ? ' is-active' : ''}`}
            onClick={() => setSourceTab('tool')}
          >
            {chartText('publishExtra.fromTool', '从说明书制作工具选择')}
          </button>
        </div>

        {sourceTab === 'tool' ? (
          <div className="tko-guide-file__tool-empty">
            <p>
              {chartText('publishExtra.toolEmpty', '「说明书制作工具」为 Tiktok 卖家中心站内能力，本工作台暂不内嵌。请在卖家中心制作并导出 PDF 后，切换到「从本地上传」。')}
            </p>
          </div>
        ) : (
          <>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,.pdf"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) void uploadPlain(f);
              }}
            />
            <input
              ref={translateInputRef}
              type="file"
              accept="application/pdf,.pdf"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) void uploadAndTranslate(f);
              }}
            />
            <div className="tko-guide-file__actions">
              <button
                type="button"
                className="tko-guide-file__upload-btn"
                disabled={busy || !shopId || list.length >= MAX_GUIDE_FILES}
                onClick={() => translateInputRef.current?.click()}
              >
                {busy && busyMode === 'translate'
                  ? chartText('publishExtra.translating', '翻译上传中…')
                  : chartText('publishExtra.translateUpload', `+ 翻译多语言并上传（${list.length}/${MAX_GUIDE_FILES}）`, { current: list.length, max: MAX_GUIDE_FILES })}
              </button>
              <button
                type="button"
                className="tko-guide-file__upload-btn"
                disabled={busy || !shopId || list.length >= MAX_GUIDE_FILES}
                onClick={() => inputRef.current?.click()}
              >
                {busy && busyMode === 'upload'
                  ? chartText('publishExtra.uploading', fixedUi("上传中…"))
                  : chartText('publishExtra.uploadFile', `+ 上传文件（${list.length}/${MAX_GUIDE_FILES}）`, { current: list.length, max: MAX_GUIDE_FILES })}
              </button>
            </div>
            <ol className="tko-guide-file__notes">
              <li>{chartText('publishExtra.guideNote', fixedUi("说明书尽可能覆盖更多语言，可有效提升转化并降低客诉。"))}</li>
              <li>
                {chartText('publishExtra.pdfLimit', '仅支持 PDF，且每个文件在 50MB 以内。')}
                <button
                  type="button"
                  className="tko-guide-file__link"
                  onClick={() => setShowRequirements((v) => !v)}
                >
                  {chartText('publishExtra.viewReqs', '查看说明书上传要求')}
                </button>
              </li>
            </ol>
            {showRequirements ? (
              <ul className="tko-guide-file__requirements">
                {GUIDE_UPLOAD_REQUIREMENTS.map((line, index) => (
                  <li key={line}>{chartText(`publishExtra.guideReq${index + 1}`, line)}</li>
                ))}
              </ul>
            ) : null}
          </>
        )}

        <ul className="tko-guide-file__list">
          {list.map((item, idx) => (
            <li key={`${item.pdfMaterialId || item.fileName}-${idx}`}>
              <span className="tko-guide-file__name" title={item.fileName}>
                {item.fileName || chartText('publishExtra.pdfNo', `PDF#${item.pdfMaterialId}`, { id: item.pdfMaterialId })}
              </span>
              <span className="tko-guide-file__langs">
                {(item.languages || []).length
                  ? (item.languages || []).join(', ')
                  : chartText('publishExtra.langPending', '语种待识别')}
                {item.translated ? chartText('publishExtra.translatedMark', ' · 已翻译') : ''}
              </span>
              <button
                type="button"
                className="tko-btn tko-btn--ghost tko-btn--sm"
                disabled={busy}
                onClick={() => onChange?.(list.filter((_, i) => i !== idx))}
              >
                {chartText('shop.delete', '删除')}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
